import { CloseIcon } from 'Components/designSystem';
import React, { MouseEventHandler, ReactNode, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import styled, { css, keyframes } from 'styled-components';

import { ReactComponent as MouseIcon } from '../../../../img/mouse.svg';
import hooks from '../../../../shared/hooks';
import { useMediaSize } from '../../hooks';
import Button, { ButtonType } from '../button/Button';
import Notification, { NotificationProps } from '../notification/Notification';
import { OverflowMenu, OverflowMenuItemProps } from '../overflowMenu';
import Headline06 from '../typography/Headline06';
import Subtitle01 from '../typography/Subtitle01';

export enum ModalWidthEnum {
    XS,
    SM,
    LG,
    XL,
    FULL
}

const MODAL_WIDTHS = {
    [ModalWidthEnum.XS]: '360px',
    [ModalWidthEnum.SM]: '696px',
    [ModalWidthEnum.LG]: '1032px',
    [ModalWidthEnum.XL]: '1368px',
    [ModalWidthEnum.FULL]: '100%'
};

export interface ModalButtonProps {
    action: () => void;
    disabled?: boolean;
    icon?: ReactNode;
    inProgress?: boolean;
    hidden?: boolean;
    title?: React.ReactNode;
    type?: ButtonType;
    withLeftIcon?: boolean;
}

export interface ModalProps {
    buttonAction?: ModalButtonProps;
    buttonAdditionalAction?: ModalButtonProps;
    buttonCancel?: ModalButtonProps & { showAsCross?: boolean; };
    buttonsInHeader?: boolean;
    children: React.ReactNode;
    className?: string;
    height?: string;
    menuItems?: OverflowMenuItemProps[];
    noPadding?: boolean;
    notification?: NotificationProps;
    subTitle?: ReactNode;
    title?: string;
    width?: ModalWidthEnum;
    onOverlayClick?: () => void;
    /**
     * Use this property to control Modal state instead of conditional Modal rendering.
     * Exit animation won't work with conditional rendering.
     * @default true
     */
    open?: boolean;
}

const TRANSITION_DURATION = 250;

const Modal: React.FC<ModalProps>  = ({
    buttonAction,
    buttonAdditionalAction,
    buttonCancel,
    buttonsInHeader = false,
    children,
    className = '',
    height = '',
    menuItems,
    noPadding = false,
    notification,
    onOverlayClick,
    open = true,
    subTitle = '',
    title,
    width = ModalWidthEnum.SM
}) => {
    const [ exited, setExited ] = useState(true);
    const [ entered, setEntered ] = useState(false);

    const withHeader = Boolean(title);

    const { isMobile } = useMediaSize();

    const fullScreen = width === ModalWidthEnum.FULL && !isMobile;
    const checkButtonsInHeader = buttonsInHeader || buttonCancel?.showAsCross || fullScreen;
    const modalRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const [ showScrollIndicator, setShowScrollIndicator ] = useState(false);

    const handleEnter = useCallback(() => {
        setExited(false);
    }, []);

    const handleEntered = useCallback(() => {
        setEntered(true);
    }, []);

    const handleExit = useCallback(() => {
        setEntered(false);
    }, []);

    const handleExited = useCallback(() => {
        setExited(true);
    }, []);

    const handleOverlayClick = useCallback<MouseEventHandler<HTMLDivElement>>((event) => {
        if (onOverlayClick && !event.defaultPrevented) {
            event.stopPropagation();
            event.preventDefault();

            onOverlayClick();
        }
    }, [ onOverlayClick ]);

    const renderActionButton = useCallback(() => {
        if (!buttonAction || buttonAction.hidden) {
            return null;
        }

        return (<ActionButton color={ buttonAction.type || 'primary' }
            isLoading={ buttonAction.inProgress }
            stretch={ Boolean(!buttonCancel || buttonCancel.hidden || buttonCancel.showAsCross) }
            onClick={ () => (typeof buttonAction.action === 'function' && buttonAction.action()) }
            label = { buttonAction.title || 'OK' }
            disabled={ buttonAction.disabled || buttonAction.inProgress }
            size="medium"
        />);
    }, [ buttonAction, buttonCancel ]);

    const renderAdditionalActionButton = () => {
        if (width === ModalWidthEnum.XS) {
            return null;
        }

        if (!buttonAdditionalAction || buttonAdditionalAction.hidden) {
            return <div />;
        }

        return (<Button color={ buttonAdditionalAction.type || 'primary' }
            onClick={ () => (typeof buttonAdditionalAction.action === 'function' && buttonAdditionalAction.action()) }
            label = { buttonAdditionalAction.title }
            disabled={ buttonAdditionalAction.disabled }
            size="medium"
            withLeftIcon={ buttonAdditionalAction.withLeftIcon }
            icon={ buttonAdditionalAction.icon }
        />);
    };

    const renderCancelButton = useCallback(() => {
        if (!buttonCancel || buttonCancel.hidden) {
            return null;
        }

        if (buttonCancel.showAsCross) {
            return (
                <IconButton onClick={ buttonCancel.action }
                    disabled={ buttonCancel.disabled }
                >
                    <CloseIcon/>
                </IconButton>
            );
        }

        return (<ActionButton color={ buttonCancel.type || 'text' }
            stretch={ width === ModalWidthEnum.XS && Boolean(!buttonAction || buttonAction.hidden) }
            onClick={ () => (typeof buttonCancel.action === 'function' && buttonCancel.action()) }
            label = { buttonCancel.title || 'Cancel' }
            disabled={ buttonCancel.disabled }
            size="medium"
        />);
    }, [ buttonCancel, buttonAction, width ]);

    const renderMenu = () => {
        if (!menuItems || menuItems.length === 0) {
            return null;
        }

        return <ModalMenu big
            isWhite
            items={ menuItems }
        />;
    };

    const renderHeader = () => {
        if (checkButtonsInHeader) {
            return (<>
                <ButtonsContainer $buttonsInHeader={ buttonsInHeader }
                    $fullScreen={ fullScreen }
                >
                    <Headline06>{ title }</Headline06>

                    <RightButtons>
                        { renderCancelButton() }

                        { buttonsInHeader && renderActionButton() }
                    </RightButtons>

                    { renderMenu() }
                </ButtonsContainer>
            </>);
        } else {
            return (title && <Header $fullScreen={ false }>{ title }</Header>);
        }
    };

    const renderSubheader = () => {
        if (!subTitle) {
            return null;
        }

        if (checkButtonsInHeader) {
            return (<PaddedSubHeader $fullScreen={ fullScreen }>{ subTitle }</PaddedSubHeader>);
        } else {
            return (<SubHeader $fullScreen={ false }>{ subTitle }</SubHeader>);
        }
    };

    const renderFooter = () => {
        if (buttonsInHeader) {
            return null;
        }

        return (<>
            {
                Boolean(notification) &&
                <Notification { ...notification } />
            }

            <ButtonsContainer $buttonsInHeader={ buttonsInHeader }
                $fullScreen={ false }>
                { renderAdditionalActionButton() }

                <RightButtons>
                    { !buttonCancel?.showAsCross && renderCancelButton() }

                    { renderActionButton() }
                </RightButtons>
            </ButtonsContainer>
        </>);
    };

    const checkScroll = useCallback(() => {
        if (contentRef && contentRef.current &&
            contentRef.current.clientHeight < contentRef.current.scrollHeight
        ) {
            setShowScrollIndicator(true);
        }
        else {
            setShowScrollIndicator(false);
        }
    }, []);

    hooks.layout.useLayoutChanged(checkScroll);

    hooks.layout.useWindowSizeChanged(checkScroll);

    hooks.layout.useLockBodyScroll(open);

    const modalElement = (
        <StyledTransitionGroup className={ className }
            appear
            enter
            exit
            $hideOverflow={ (open && !entered) || (!open && !exited) }
        >
            { open && (
                <Transition mountOnEnter
                    unmountOnExit
                    timeout={ TRANSITION_DURATION }
                    onExited={ handleExited }
                    onEnter={ handleEnter }
                    onEntered={ handleEntered }
                    onExit={ handleExit }
                    nodeRef={ modalRef }
                >
                    { (status) => {
                        const visible = status === 'entering' || status === 'entered';

                        return (
                            <>
                                <Overlay clickable={ Boolean(onOverlayClick) }
                                    onClick={ handleOverlayClick }
                                    $visible={ visible }
                                />
                                <ModalRoot height={ height }
                                    $buttonsInHeader={ checkButtonsInHeader }
                                    $fullScreen={ fullScreen }
                                    $visible={ visible }
                                    width={ MODAL_WIDTHS[width] }
                                    ref={ modalRef }
                                >
                                    { showScrollIndicator && (
                                        <StyledMouseIcon $buttonsInHeader={ buttonsInHeader }
                                            $fullScreen={ fullScreen }
                                        />
                                    ) }

                                    <Top $buttonsInHeader={ checkButtonsInHeader }>
                                        { renderHeader() }

                                        { renderSubheader() }
                                    </Top>

                                    <Content $buttonsInHeader={ checkButtonsInHeader }
                                        $fullScreen={ fullScreen }
                                        noPadding={ noPadding }
                                        withHeader={ withHeader }
                                        ref={ contentRef }
                                        data-scroll-area="0px 0px 0px 0px"
                                    >
                                        { children }
                                    </Content>

                                    { renderFooter() }
                                </ModalRoot>
                            </>
                        );
                    } }
                </Transition>
            ) }
        </StyledTransitionGroup>
    );

    if (!open && exited) {
        return null;
    }

    const domElement = document.getElementById('globus-design-system-portal-root');

    if (!domElement) {
        return modalElement;
    }

    return createPortal(
        modalElement,
        domElement
    );
};

export default Modal;

const slide = keyframes`
    100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(2px);
    }
    0% {
        transform: translateY(0);
    }
`;

const StyledMouseIcon = styled(MouseIcon)<{ $buttonsInHeader?: boolean; $fullScreen?: boolean }>`
    bottom: ${ ({ $buttonsInHeader, $fullScreen, theme }) => ($buttonsInHeader || $fullScreen ? 'inherit' : theme.size.size_100) };
    top: ${ ({ $buttonsInHeader, $fullScreen, theme }) => ($buttonsInHeader || $fullScreen ? theme.size.size_100 : 'inherit') };
    position: absolute;
    right: 0;
    width: ${ ({ theme }) => theme.size.size_30 };
    height: ${ ({ theme }) => theme.size.size_30 };

    path {
        &:last-of-type {
            animation: ${ slide } 1s ease-in-out infinite;
        }
    }
`;

const StyledTransitionGroup = styled(TransitionGroup)<{ $hideOverflow?: boolean }>`
    align-items: center;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    overflow-y: auto;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 1000;

    ${ ({ $hideOverflow }) => $hideOverflow && `overflow: hidden;` }
`;

const Overlay = styled.div<{ clickable?: boolean; $visible?: boolean }>`
    position: fixed;
    background-color: ${ ({ theme }) => theme.semanticColor.overlay_01 };
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    transition: opacity ${ TRANSITION_DURATION }ms ease-in;
    opacity: ${ ({ $visible }) => ($visible ? 1 : 0) };

    ${ ({ clickable }) => clickable && `cursor: pointer;` }
`;

interface RootProps {
    $buttonsInHeader: boolean;
    $fullScreen?: boolean;
    height: string;
    width: string;
    $visible?: boolean;
}

const scaleStyle = css<{ $visible?: boolean }>`
    transition: opacity 250ms ease-in,
                transform 250ms ease-in;
    opacity: ${ ({ $visible }) => ($visible ? 1 : 0) };
    transform: ${ ({ $visible }) => ($visible ? `scale(1)` : `scale(0)`) };
`;

const slideInStyle = css<{ $visible?: boolean }>`
    transition: transform 250ms ease-in;
    opacity: 1;
    transform: ${ ({ $visible }) => ($visible ? `translateY(0)` : `translateY(100%)`) };
`;

const ModalRoot = styled.div<RootProps>`
    background-color: ${ ({ theme }) => theme.color.white };
    border-radius: ${ ({ theme }) => theme.radius.radius_10 };
    display: flex;
    flex-direction: column;
    height: ${ ({ $fullScreen, height }) => ($fullScreen ? '100%' : height || 'auto') };
    margin: 0 auto;
    max-height: ${ ({ theme, $fullScreen }) => ($fullScreen ? 'none' : `calc(100vh - ${ theme.size.size_25 })`) };
    max-width: 100%;
    padding: ${ ({ theme }) => theme.size.size_30 } 0;
    position: relative;
    width: ${ ( { width } ) => width };

    ${ ({ $buttonsInHeader }) => ($buttonsInHeader ? 'padding: 0;' : 'padding-bottom: 0;') };

    ${ scaleStyle }

    @media ${ ({ theme }) => theme.breakpoints.mediaQueries.isMobile } {
        height: auto;
        max-height: 100%;
        align-self: flex-end;
        border-bottom-left-radius: unset;
        border-bottom-right-radius: unset;

        ${ slideInStyle }
    }
`;

const ButtonsContainer = styled.div<{ $fullScreen: boolean; $buttonsInHeader: boolean }>`
    align-items: center;
    border-radius: ${ ({ $buttonsInHeader, $fullScreen, theme }) => ($buttonsInHeader || $fullScreen ? `${ theme.radius.radius_10 } ${ theme.radius.radius_10 } 0 0` : `0 0 ${ theme.radius.radius_10 } ${ theme.radius.radius_10 }`) };
    display: flex;
    height: ${ ({ theme }) => theme.size.size_90 };
    justify-content: space-between;
    padding:  ${ ({ theme, $fullScreen }) => `${ theme.size.size_30 } ${ $fullScreen ? theme.size.size_40 : theme.size.size_30 } ` };
    white-space: nowrap;

    @media(max-width: ${ ({ theme }) => theme.breakpoints.tablet }) {
        padding: ${ ({ theme }) => `${ theme.size.size_30 } ${ theme.size.size_20 }` };
    }
`;

const RightButtons = styled.div`
    align-items: center;
    display: flex;
    justify-content: flex-end;
    width: 100%;
    gap: ${ ({ theme }) => theme.size.size_30 };
`;
interface ButtonProps {
    stretch: boolean;
}

const ActionButton = styled(Button)<ButtonProps>`
    width: ${ ( { stretch } ) => (stretch ? '100%': 'auto') };
`;

interface ContentProps {
    $fullScreen: boolean;
    $buttonsInHeader: boolean;
    noPadding?: boolean;
    withHeader?: boolean;
}

const Content = styled.div<ContentProps>`
    color: ${ ({ theme }) => theme.color.grey.grey_80 };
    max-height: 100%;
    overflow-y: auto;
    padding-bottom: ${ ({ theme, $buttonsInHeader }) => ($buttonsInHeader ? theme.size.size_30 : theme.size.size_40 ) };
    padding-left: ${ ({ theme, $fullScreen, noPadding }) => (noPadding ? 0 : $fullScreen ? theme.size.size_40 : theme.size.size_30 ) };
    padding-right: ${ ({ theme, $fullScreen, noPadding }) => (noPadding ? 0 : $fullScreen ? theme.size.size_40 : theme.size.size_30 ) };
    padding-top: ${ ({ theme, withHeader }) => withHeader && theme.size.size_15 };

    @media(max-width: ${ ({ theme }) => theme.breakpoints.tablet }) {
        padding-left: ${ ({ theme }) => theme.size.size_30 };
        padding-right:  ${ ({ theme }) => theme.size.size_30 };
    }
`;

const Top = styled.div<{ $buttonsInHeader?: boolean }>`
    padding-bottom: ${ ({ $buttonsInHeader, theme }) => ($buttonsInHeader ? 0 : theme.size.size_15) };
`;

const Header = styled(Headline06)<{ $fullScreen: boolean }>`
    padding: 0 ${ ({ theme, $fullScreen }) => ( $fullScreen ? theme.size.size_40 : theme.size.size_30 ) };
    color: ${ ({ theme }) => theme.color.grey.grey_80 };

    @media(max-width: ${ ({ theme }) => theme.breakpoints.tablet }) {
        padding: 0 ${ ({ theme }) => theme.size.size_20 };
    }
`;

const SubHeader = styled(Subtitle01)<{ $fullScreen: boolean }>`
    color: ${ ({ theme }) => theme.color.grey.grey_80 };
    margin-top: ${ ({ theme }) => theme.size.size_10 };
    padding: 0 ${ ({ theme, $fullScreen }) => ( $fullScreen ? theme.size.size_40 : theme.size.size_30 ) };

    @media(max-width: ${ ({ theme }) => theme.breakpoints.tablet }) {
        padding: 0 ${ ({ theme }) => theme.size.size_20 };
    }
`;

const PaddedSubHeader = styled(SubHeader)`
    margin-top: ${ ({ theme }) => theme.size.size_30 };
`;

const ModalMenu = styled(OverflowMenu)`
    margin-left: ${ ({ theme }) => theme.size.size_20 };

    @media(max-width: ${ ({ theme }) => theme.breakpoints.tablet }) {
        margin-left: ${ ({ theme }) => theme.size.size_5 };
    }
`;

const IconButton = styled.button`
    display: block;
    cursor: pointer;
    position: relative;
    width: ${ ({ theme }) => theme.size.size_30 };
    height: ${ ({ theme }) => theme.size.size_30 };
    border: none;
    background-color: transparent;
    padding: ${ ({ theme }) => theme.size.size_2 };

    &:before {
        display: none;
        background-color: ${ ({ theme }) => theme.color.grey.grey_50 };
        opacity: 0.1;
        border-radius: 50%;
        content: '';
        position: absolute;
        top: ${ ({ theme }) => `-${ theme.size.size_5 }` };
        left: ${ ({ theme }) => `-${ theme.size.size_5 }` };
        height: ${ ({ theme }) => `calc(100% + ${ theme.size.size_10 })` };
        width: ${ ({ theme }) => `calc(100% + ${ theme.size.size_10 })` };
    }

    &:not(:disabled):hover:before {
        display: block;
    }

    &:disabled {
        cursor: not-allowed;
    }
`;
